import { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import {
  Shield,
  Lock,
  Video,
  ArrowUpRight,
  Loader2,
  AlertTriangle,
} from "lucide-react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Header = () => (
  <header className="border-b border-black/5 bg-[#FDFCF8]/90 backdrop-blur-xl sticky top-0 z-40">
    <div className="max-w-5xl mx-auto px-6 md:px-10 py-5 flex items-center justify-between">
      <Link
        to="/"
        data-testid="join-back-home"
        className="flex items-center group"
      >
        <img
          src="/logo.jpg"
          alt="Neurological Communication Partners"
          className="h-14 w-auto rounded-xl ring-1 ring-black/5"
        />
      </Link>
      <span className="hidden md:inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-[#5C6661]">
        <Shield size={14} strokeWidth={1.5} className="text-[#739072]" />
        Secure Telehealth
      </span>
    </div>
  </header>
);

export default function JoinSession() {
  const [params] = useSearchParams();
  const meetingFromUrl = params.get("meeting") || "";

  const [meeting, setMeeting] = useState(meetingFromUrl);
  const [initials, setInitials] = useState("");
  const [validating, setValidating] = useState(false);
  const [valid, setValid] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const [hipaa, setHipaa] = useState(false);
  const [gdpr, setGdpr] = useState(false);
  const [recording, setRecording] = useState(false);

  const allConsented = hipaa && gdpr && recording;
  const canSubmit = useMemo(
    () => allConsented && initials.trim().length >= 1 && valid === true,
    [allConsented, initials, valid]
  );

  useEffect(() => {
    if (!meeting) {
      setValid(null);
      return;
    }
    let cancelled = false;
    const t = setTimeout(async () => {
      setValidating(true);
      try {
        const r = await axios.get(`${API}/sessions/validate-link`, {
          params: { meeting },
        });
        if (!cancelled) setValid(!!r.data.valid);
      } catch {
        if (!cancelled) setValid(false);
      } finally {
        if (!cancelled) setValidating(false);
      }
    }, 350);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [meeting]);

  const buildLaunchUrl = (m) => {
    const v = m.trim();
    if (/^\d{9,11}$/.test(v)) return `https://zoom.us/j/${v}`;
    return v;
  };

  const handleLaunch = async (e) => {
    e.preventDefault();
    setError("");
    if (!canSubmit) {
      setError(
        "Please enter your initials, paste your meeting link, and acknowledge all consents."
      );
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/sessions/consent`, {
        patient_initials: initials.trim().toUpperCase(),
        meeting_ref: meeting.trim(),
        consent_hipaa: true,
        consent_gdpr: true,
        consent_recording_disclosure: true,
      });
      window.open(buildLaunchUrl(meeting), "_blank", "noopener,noreferrer");
    } catch (err) {
      setError(
        err?.response?.data?.detail?.[0]?.msg ||
          "Could not record consent. Please try again or contact Kathleen."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFCF8] text-[#2D332F]">
      <Header />

      <main className="max-w-5xl mx-auto px-6 md:px-10 py-14 md:py-20">
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <p className="text-xs uppercase tracking-[0.25em] text-[#5C6661] mb-4 flex items-center gap-3">
            <span className="w-8 h-px bg-[#739072]" /> Join your secure session
          </p>
          <h1
            data-testid="join-title"
            className="font-serif text-4xl md:text-5xl font-light tracking-tight leading-[1.05]"
          >
            Welcome. Let&rsquo;s confirm a few things before we connect.
          </h1>
          <p className="mt-6 text-[#5C6661] text-lg leading-relaxed max-w-2xl">
            Sessions are delivered through{" "}
            <strong>Zoom for Healthcare</strong>, a HIPAA-eligible platform
            covered by a Business Associate Agreement (BAA) with Kathleen
            McGrath, M.S. CCC-SLP. EU/Ireland sessions are processed under GDPR.
          </p>
        </motion.div>

        <div className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-8">
          <form
            onSubmit={handleLaunch}
            className="lg:col-span-7 rounded-3xl border border-black/5 bg-white p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)]"
            data-testid="join-form"
          >
            <h2 className="font-serif text-2xl text-[#2D332F] mb-6">
              Session details
            </h2>

            <div className="space-y-5">
              <div>
                <Label htmlFor="initials" className="text-[#2D332F]">
                  Your initials
                </Label>
                <Input
                  id="initials"
                  data-testid="join-initials"
                  value={initials}
                  onChange={(e) => setInitials(e.target.value)}
                  maxLength={10}
                  placeholder="e.g. K.M."
                  required
                  className="mt-1.5"
                />
                <p className="text-xs text-[#5C6661] mt-1.5">
                  Used only to log your consent. Full name not stored on this
                  page.
                </p>
              </div>

              <div>
                <Label htmlFor="meeting" className="text-[#2D332F]">
                  Zoom meeting link or ID (sent by Kathleen)
                </Label>
                <Input
                  id="meeting"
                  data-testid="join-meeting"
                  value={meeting}
                  onChange={(e) => setMeeting(e.target.value)}
                  placeholder="https://zoom.us/j/123456789  or  1234567890"
                  required
                  className="mt-1.5"
                />
                <div className="mt-2 text-xs flex items-center gap-2">
                  {validating && (
                    <span className="text-[#5C6661] inline-flex items-center gap-1.5">
                      <Loader2 size={12} className="animate-spin" /> Checking…
                    </span>
                  )}
                  {!validating && valid === true && (
                    <span
                      className="text-[#5C765B]"
                      data-testid="join-link-valid"
                    >
                      ✓ Looks like a valid Zoom link
                    </span>
                  )}
                  {!validating && valid === false && meeting && (
                    <span
                      className="text-amber-700 inline-flex items-center gap-1.5"
                      data-testid="join-link-invalid"
                    >
                      <AlertTriangle size={12} /> This doesn&rsquo;t look like
                      a Zoom link. Please check the message Kathleen sent.
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div className="mt-8 rounded-2xl border border-black/5 bg-[#F3F1E7] p-5 space-y-4">
              <h3 className="font-serif text-lg text-[#2D332F]">
                Consent &amp; acknowledgement
              </h3>
              <label className="flex items-start gap-3 cursor-pointer">
                <Checkbox
                  checked={hipaa}
                  onCheckedChange={(v) => setHipaa(!!v)}
                  data-testid="join-consent-hipaa"
                  className="mt-0.5"
                />
                <span className="text-sm text-[#2D332F] leading-relaxed">
                  <strong>HIPAA (USA).</strong> I consent to receiving telehealth
                  services and to my Protected Health Information being handled
                  in accordance with HIPAA and Kathleen&rsquo;s{" "}
                  <Link
                    to="/privacy"
                    className="underline text-[#739072]"
                    target="_blank"
                  >
                    Privacy Notice
                  </Link>
                  .
                </span>
              </label>
              <label className="flex items-start gap-3 cursor-pointer">
                <Checkbox
                  checked={gdpr}
                  onCheckedChange={(v) => setGdpr(!!v)}
                  data-testid="join-consent-gdpr"
                  className="mt-0.5"
                />
                <span className="text-sm text-[#2D332F] leading-relaxed">
                  <strong>GDPR (EU/Ireland).</strong> I consent to my personal
                  data being processed for the purpose of providing speech
                  &amp; language therapy, with the legal bases described in the
                  Privacy Notice.
                </span>
              </label>
              <label className="flex items-start gap-3 cursor-pointer">
                <Checkbox
                  checked={recording}
                  onCheckedChange={(v) => setRecording(!!v)}
                  data-testid="join-consent-recording"
                  className="mt-0.5"
                />
                <span className="text-sm text-[#2D332F] leading-relaxed">
                  <strong>Recording disclosure.</strong> I understand sessions
                  are <em>not</em> recorded by default. Any recording requires
                  explicit verbal consent at the start of the session.
                </span>
              </label>
            </div>

            {error && (
              <p
                role="alert"
                data-testid="join-error"
                className="mt-5 text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl p-3"
              >
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={!canSubmit || submitting}
              data-testid="join-launch"
              className="mt-6 w-full inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full bg-[#2D332F] text-[#FDFCF8] text-sm tracking-wide hover:bg-[#739072] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? (
                <>
                  <Loader2 size={16} className="animate-spin" /> Connecting…
                </>
              ) : (
                <>
                  Launch secure Zoom session{" "}
                  <ArrowUpRight size={16} strokeWidth={1.5} />
                </>
              )}
            </button>
            <p className="mt-3 text-xs text-[#5C6661] text-center">
              The session opens in a new tab. Keep this tab open until your
              session ends.
            </p>
          </form>

          <aside className="lg:col-span-5 space-y-5">
            <div
              className="rounded-3xl border border-black/5 bg-[#2D332F] text-[#FDFCF8] p-7"
              data-testid="join-security-card"
            >
              <Shield
                size={20}
                strokeWidth={1.5}
                className="text-[#D4B996] mb-4"
              />
              <h3 className="font-serif text-2xl mb-3">
                What makes this secure
              </h3>
              <ul className="space-y-3 text-sm text-[#FDFCF8]/80">
                <li className="flex gap-3">
                  <Lock
                    size={16}
                    strokeWidth={1.5}
                    className="text-[#D4B996] mt-0.5 shrink-0"
                  />
                  <span>
                    End-to-end encrypted Zoom for Healthcare meetings, covered
                    by an active BAA.
                  </span>
                </li>
                <li className="flex gap-3">
                  <Shield
                    size={16}
                    strokeWidth={1.5}
                    className="text-[#D4B996] mt-0.5 shrink-0"
                  />
                  <span>
                    GDPR-aligned consent capture &amp; data minimisation —
                    we hash IPs and store only the audit trail we need.
                  </span>
                </li>
                <li className="flex gap-3">
                  <Video
                    size={16}
                    strokeWidth={1.5}
                    className="text-[#D4B996] mt-0.5 shrink-0"
                  />
                  <span>
                    Sessions are not recorded unless you provide explicit
                    verbal consent.
                  </span>
                </li>
              </ul>
            </div>

            <div className="rounded-3xl border border-black/5 bg-[#FDFCF8] p-7">
              <p className="text-xs uppercase tracking-[0.22em] text-[#5C6661] mb-3">
                Pre-flight tips
              </p>
              <ul className="space-y-2 text-sm text-[#2D332F]/85 list-disc pl-4">
                <li>Use Chrome, Edge or the Zoom desktop client.</li>
                <li>
                  Test your audio &amp; camera before the session via{" "}
                  <a
                    className="underline text-[#739072]"
                    href="https://zoom.us/test"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    zoom.us/test
                  </a>
                  .
                </li>
                <li>Find a quiet, well-lit, private space.</li>
                <li>
                  Use headphones — improves intelligibility for voice work.
                </li>
              </ul>
            </div>
          </aside>
        </div>
      </main>
    </div>
  );
}
